import { useMemo } from "react";

import { findKey, pick } from "lodash";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { TagType } from "~/components/tag/types";
import { Variables } from "~/constants/request";
import { useVariable } from "~/context/variables/hooks/use-variable";
import { useAzureConfigurator } from "~/services/azure-configurator/use-azure-configurator";

export interface ProductTag {
  key: TagType;
  title: string;
  textColor?: string;
  backgroundColor?: string;
}
interface Input {
  maximumTag?: number;
  data: Partial<Record<TagType, unknown>>;
}

export const useProductTags = ({ data, maximumTag = 2 }: Input): ProductTag[] => {
  const productTags = useVariable<{ key: TagType; title: string }[]>(
    Variables.PRODUCT_TAGS,
  );
  const { locale } = useParams<GlobalPathParams>();
  const config = useAzureConfigurator(locale);
  const isClickAndCollectEnabled = useMemo(
    () => config?.featureFlags?.clickAndCollect?.enabled,
    [config],
  );
  const clickAndCollectTagKeys = pick(TagType, [
    "clickAndCollect",
    "clickAndCollectExclusive",
  ]);

  const tags = useMemo(
    () =>
      productTags
        ?.filter((tag) => {
          const key = findKey(TagType, function (value) {
            return tag.key === value;
          });

          if (key === TagType.coords.toLowerCase()) {
            return false;
          }

          if (key === TagType.comingSoon) {
            return false;
          }

          if (key === TagType.primarkCares) {
            return false;
          }

          if (
            !isClickAndCollectEnabled &&
            key !== undefined &&
            (key === "clickAndCollect" || key === "clickAndCollectExclusive") &&
            clickAndCollectTagKeys?.[key]
          ) {
            return false;
          }
          if (key !== undefined && data?.[key as TagType]) {
            return tag;
          }
          return false;
        })
        ?.slice(0, maximumTag),
    [
      productTags,
      clickAndCollectTagKeys,
      data,
      isClickAndCollectEnabled,
      maximumTag,
    ],
  );

  if (!productTags?.length) {
    return [];
  }

  return tags;
};
